import { useLayout } from '@/components/datamodel/hooks/useLayout';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getSecondaryItemTitleFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttribute, PersistedItem, SecondaryTitleAttributeConfig } from '@/types/DataModel';

type SecondaryTitleMode = 'filter' | 'attributes' | 'none';

interface ResolvedAttributeConfig {
    attribute: DataModelAttribute;
    valuesFormat: ('icon' | 'label' | 'key')[];
}

interface UseSecondaryItemTitleResult {
    mode: SecondaryTitleMode;
    filterHtml?: string;
    attributeConfigs?: ResolvedAttributeConfig[];
    /** Whether there is content to display */
    hasContent: boolean;
}

interface UseSecondaryItemTitleProps {
    item?: PersistedItem;
}

const DEFAULT_VALUES_FORMAT: ('icon' | 'label' | 'key')[] = ['label', 'key'];
const DISALLOWED_TYPE_NAMES = ['Physical', 'Dimensional', 'MultiDimensional'];
/**
 * Figma spec caps the secondary header row at three `LabelValueAttribute`
 * entries. Anything beyond the third configured attribute is dropped with a
 * warning so mis-configured data models degrade predictably instead of
 * silently overflowing.
 */
const MAX_ATTRIBUTE_CONFIGS = 3;

export const useSecondaryItemTitle = ({ item }: UseSecondaryItemTitleProps): UseSecondaryItemTitleResult => {
    const { layout } = useLayout();
    const { dataModel } = useDataModel();
    const { user } = useUser();
    const { organization } = useOrganization();

    // Get the config - check both camelCase and snake_case
    const layoutOptions = layout?.layoutOptions as Record<string, unknown> | undefined;
    const config = layoutOptions?.secondaryItemTitleFilter ?? layoutOptions?.secondary_item_title_filter;

    // No config defined
    if (!config) {
        return { mode: 'none', hasContent: false };
    }

    // MODE 1: String filter name (like itemTitleFilter)
    if (typeof config === 'string') {
        const customFilter = getSecondaryItemTitleFilter(config);
        if (!customFilter) {
            logger.error(`Secondary item title filter '${config}' was not found.`);
            return { mode: 'none', hasContent: false };
        }

        if (!item) {
            return { mode: 'filter', filterHtml: '', hasContent: false };
        }

        try {
            const filterHtml = customFilter(item, user, organization);
            const hasContent = !!filterHtml && filterHtml.trim().length > 0;
            return { mode: 'filter', filterHtml: filterHtml || '', hasContent };
        } catch (error) {
            logger.error(`Secondary item title filter '${config}' threw an error:`, error);
            return { mode: 'filter', filterHtml: '', hasContent: false };
        }
    }

    // MODE 2: Array of attribute configs
    if (Array.isArray(config)) {
        const resolvedConfigs: ResolvedAttributeConfig[] = [];

        for (const attrConfig of config as (SecondaryTitleAttributeConfig & { values_format?: ('icon' | 'label' | 'key')[] })[]) {
            const attribute = dataModel?.attribute(attrConfig.name);

            if (!attribute) {
                logger.warn(`Secondary item title: attribute '${attrConfig.name}' not found in data model.`);
                continue;
            }

            if (attribute.typeName && DISALLOWED_TYPE_NAMES.includes(attribute.typeName)) {
                logger.warn(
                    `Secondary item title: attribute '${attrConfig.name}' has type '${attribute.typeName}' which is not allowed. Skipping.`
                );
                continue;
            }

            // Handle both camelCase and snake_case for valuesFormat
            const valuesFormat = attrConfig.valuesFormat ?? attrConfig.values_format ?? DEFAULT_VALUES_FORMAT;

            resolvedConfigs.push({
                attribute,
                valuesFormat,
            });
        }

        if (resolvedConfigs.length > MAX_ATTRIBUTE_CONFIGS) {
            logger.warn(
                `Secondary item title: ${resolvedConfigs.length} attributes configured, ` +
                `but only the first ${MAX_ATTRIBUTE_CONFIGS} will be rendered ` +
                '(Figma spec caps the secondary header at 3 attributes).',
            );
        }
        const cappedConfigs = resolvedConfigs.slice(0, MAX_ATTRIBUTE_CONFIGS);

        const hasContent = cappedConfigs.length > 0;
        return { mode: 'attributes', attributeConfigs: cappedConfigs, hasContent };
    }

    logger.warn('Secondary item title filter has invalid configuration type.');
    return { mode: 'none', hasContent: false };
};
